import React from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { toUpper } from 'lodash';
import numeral from 'numeral';

import Super from '@/comp/Super';
import { useMeasurement } from '@/hooks/useMeasurement';
import { formatThousands } from '@/utils/util-functions';
import { GlobalToken } from 'antd';

interface SpecificPowerTooltipProps {
  value: number;
  actualEnergy: number;
  volume: number;
  name: string;
  measurementSystem: string;
  token: GlobalToken;
}

const SpecificPowerTooltip = ({
  value,
  actualEnergy,
  volume,
  name,
  measurementSystem,
  token,
}: SpecificPowerTooltipProps) => {
  const { t } = useTranslation();
  const { tunit } = useMeasurement();

  return (
    <TooltipContainer theme={{ token }}>
      <div className="title">{name}</div>

      <table>
        <tr className="first-row">
          <td>
            <div className="name">{t('Specific Power')}</div>
          </td>
          <td>
            <div className="value-container">
              <span className="value">
                {toUpper(measurementSystem) === 'IMPERIAL'
                  ? numeral(value).format('0,0.0')
                  : numeral(value).format('0,0.00')}
              </span>
              <span className="label">
                <Trans i18nKey={tunit('kW/(m3/min)')}>
                  <Super />
                </Trans>
              </span>
            </div>
          </td>
        </tr>
        <tr className="spacer">
          <td></td>
          <td></td>
        </tr>
        <tr>
          <td>
            <div className="name">{t('Energy')}</div>
          </td>
          <td>
            <div className="value-container">
              <span className="value">{formatThousands(actualEnergy)}</span>
              <span className="label">{tunit('kWh')}</span>
            </div>
          </td>
        </tr>
        <tr>
          <td>
            <div className="name">{t('Volume')}</div>
          </td>
          <td>
            <div className="value-container">
              <span className="value">{formatThousands(volume)}</span>
              <span className="label">
                <Trans i18nKey={tunit('m^3')}>
                  <Super />
                </Trans>
              </span>
            </div>
          </td>
        </tr>
      </table>
    </TooltipContainer>
  );
};

const TooltipContainer = styled.div<{ theme: { token: GlobalToken } }>`
  position: relative;
  padding: ${({ theme: { token } }) => token.paddingMD}px ${({ theme: { token } }) => token.paddingLG}px;
  background-color: ${({ theme: { token } }) => token.colorBgElevated};
  color: ${({ theme: { token } }) => token.colorText};
  border-radius: 4px;
  box-shadow: ${({ theme: { token } }) => token.boxShadowSecondary};

  .title {
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    padding-bottom: ${({ theme: { token } }) => token.paddingSM}px;
  }

  table {
    tbody {
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    }

    tr {
      padding: ${({ theme: { token } }) => token.paddingXXS}px 0;

      &.first-row {
        td {
          background-color: ${({ theme: { token } }) => token.colorBgElevated};

          .name {
            font-size: ${({ theme: { token } }) => token.fontSize}px;
            font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
          }

          .value-container {
            align-items: baseline;
            .value {
              font-size: ${({ theme: { token } }) => token.fontSize}px;
              font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
            }
          }

          &:first-child {
            border-radius: 2px 0 0 2px;
          }

          &:last-child {
            border-radius: 0 2px 2px 0;
          }
        }
      }

      td {
        position: relative;
        padding: ${({ theme: { token } }) => token.paddingXXS}px ${({ theme: { token } }) => token.paddingXS}px;

        .value-container {
          .label {
            padding-left: ${({ theme: { token } }) => token.paddingXXS}px;
          }
        }

        .sign {
          position: absolute;
          left: -15px;
          top: 6px;
        }
      }
    }

    .green {
      color: ${({ theme: { token } }) => token.colorSuccess};
    }
  }
`;

export default SpecificPowerTooltip;
